/**
 * @file Weapon archetypes (GDD §Fase 3 — nuevas armas).
 *
 * A weapon is data, never a class. Adding one means appending an entry here:
 * the combat system, the bullet factory and the prefab registry all read the
 * table, so no code changes anywhere.
 *
 * Fields mirror {@link ../components/Weapon.js Weapon} plus a `projectile`
 * block describing how the round looks — visuals stay out of the stat block so
 * the same shell can be re-skinned per weapon.
 *
 * Ammunition is part of the row too: magazine size, reserve and reload timing
 * are what separate a sidearm from a rifle far more than damage does. Every
 * player weapon ships as `fireMode: 'auto'` — holding the trigger keeps the
 * weapon firing at its own cadence, which is how the game has always played;
 * `'semi'` (one shot per press, the component default) is a per-row opt-in.
 */

import { Layer } from './Layers.js';
import { PALETTE } from './Palette.js';

/**
 * Firing model of a weapon. `standard` rounds are spawned by the combat system;
 * `explosive_heavy` ones carry ammo and a blast, so the rocket system owns them
 * instead. The category is what routes a weapon to its firing system.
 * @readonly
 * @enum {string}
 */
export const WeaponCategory = Object.freeze({
  STANDARD: 'standard',
  EXPLOSIVE_HEAVY: 'explosive_heavy'
});

/**
 * @typedef {object} ProjectileVisual
 * @property {string} prefab Prefab key registered for this round.
 * @property {number} color Emissive colour.
 * @property {number} radius Mesh radius.
 * @property {number} length Mesh length along the travel axis.
 * @property {number} [flashColor] Muzzle flash colour; defaults to the palette's.
 * @property {number} [flashIntensity] Muzzle flash peak intensity.
 * @property {number} [flashLife] Muzzle flash lifetime in seconds.
 */

/**
 * @typedef {object} LaserBlock
 * @property {number} color Emissive colour.
 * @property {number} width Beam thickness in world units.
 * @property {number} opacity Beam material opacity.
 */

/**
 * @typedef {object} AmmoBlock
 * @property {number} magazineCapacity Rounds held by a full magazine.
 * @property {number} startingAmmo Rounds in the magazine when equipped.
 * @property {number} reserveAmmo Rounds available to reload from.
 * @property {boolean} reloadEnabled Whether the weapon reloads at all.
 * @property {number} reloadDuration Seconds a reload takes.
 * @property {boolean} infiniteAmmo When true, firing never spends a round.
 */

/**
 * @typedef {object} ExplosiveBlock
 * @property {number} radius Blast radius in world units.
 * @property {number} damage Damage at the centre; falls off linearly to zero.
 * @property {number} selfDamageMultiplier Fraction of the blast the shooter takes.
 * @property {number} mask Collision layers the blast may damage.
 * @property {number} shake Camera trauma added by the detonation, 0..1.
 */

/**
 * How a reload reads to the player (issue #78). Data only — the HUD, audio and
 * any reload animation branch on `style`, never on the weapon key.
 * @typedef {object} ReloadPresentation
 * @property {'magazine'|'single_round'|'heavy_round'|'canister'} style Feel of
 *   the reload: a whole magazine/cell swap, cartridges seated one by one, heavy
 *   shells, or a pressurised canister.
 * @property {boolean} [finalClack] Whether a closing "seated" cue plays when the
 *   reload completes; defaults to true.
 */

/**
 * @typedef {object} TrailBlock
 * @property {number} distance World units between puffs.
 * @property {number} color Puff colour.
 * @property {number} size Puff radius.
 * @property {number} life Puff lifetime in seconds.
 */

/**
 * @typedef {object} WeaponArchetype
 * @property {string} name Display name for the HUD.
 * @property {number} modelScale Multiplier applied to the 3D weapon asset while
 *   a character is holding it.
 * @property {string} [category] Firing model; defaults to `standard`.
 * @property {number} damage Damage per projectile.
 * @property {number} fireRate Shots per second.
 * @property {number} [burstCount] Rounds fired by one accepted trigger request.
 * @property {number} [burstInterval] Seconds between rounds inside a burst.
 * @property {number} [shotCooldownSeconds] Explicit floor on the time between
 *   shots, for weapons whose cadence is a design statement rather than a rate.
 * @property {number} projectileSpeed Speed in units/second.
 * @property {number} range Maximum travel distance, and the hitscan trace length.
 * @property {number} projectileRadius Collider radius of each round.
 * @property {number} spread Cone half-angle in radians.
 * @property {number} pellets Projectiles released per shot.
 * @property {number} pierce Targets each round passes through.
 * @property {number} muzzleForward Muzzle offset along the facing axis.
 * @property {number} muzzleHeight Muzzle height above the origin.
 * @property {number} projectileLayer Collision layer of the rounds.
 * @property {number} projectileMask Layers the rounds may hit.
 * @property {number} shake Camera trauma added per shot, 0..1.
 * @property {number} [knockback] Multiplier on the shove each hit delivers,
 *   over `CONFIG.knockback.bulletImpulse`. Omit for 1; 0 makes a weapon push
 *   nothing at all.
 * @property {number} [maxDamagePerTarget] Damage ceiling per target per shot.
 * @property {number} [falloffStart] Fraction of the range where damage starts falling, 0..1.
 * @property {number} [falloffMin] Damage multiplier at maximum range, 0..1.
 * @property {number} [muzzleParticles] Muzzle particles per shot; omit for the global default.
 * @property {number} [magazineCapacity] Rounds per magazine; omit (0) for a
 *   weapon that never runs dry.
 * @property {number} [startingAmmo] Rounds loaded on pickup; defaults to a full
 *   magazine and is clamped to `magazineCapacity`.
 * @property {number} [reserveAmmo] Spare rounds available after the loaded
 *   magazine. Must be finite, so debug ammunition caps stay editable.
 * @property {boolean} [reloadEnabled] Whether the weapon can be reloaded.
 * @property {number} [reloadDuration] Seconds a full reload takes.
 * @property {boolean} [infiniteAmmo] Skips the magazine entirely, for carriers
 *   that must never stop shooting (enemies).
 * @property {number} [projectileLifetime] Seconds a round may stay alive.
 * @property {ExplosiveBlock} [explosive] Blast carried by each round; the
 *   executor spawns a rocket instead of a bullet when set.
 * @property {TrailBlock} [trail] Trail emitted by each round.
 * @property {ProjectileVisual} projectile Round visuals.
 * @property {string} [type] `'melee'` marks a weapon that swings instead of
 *   shooting: {@link ../systems/MeleeWeaponSystem.js MeleeWeaponSystem} is its
 *   trigger, the way the stream system is a streaming weapon's.
 * @property {number} [meleeArcDegrees] Full width of the swing arc, in degrees.
 * @property {number} [swingSeconds] How long one swing takes to read on screen.
 * @property {boolean} [fallback] Re-issued automatically whenever its carrier
 *   ends up empty-handed. At most one row should set this.
 * @property {boolean} [droppable] False for a weapon that must never end up on
 *   the floor — swapping away from it simply unequips it.
 * @property {boolean} [throwable] False for a weapon `T` cannot throw.
 * @property {LaserBlock} [laser] Persistent aim-line visual while equipped.
 * @property {string} [shotType] `'projectile'` (default) or `'hitscan'`.
 * @property {string} [fireMode] `'semi'` (default) or `'auto'`.
 * @property {number} [magazineCapacity] Rounds per magazine.
 * @property {number} [startingAmmo] Rounds loaded on spawn; defaults to a full magazine.
 * @property {number} [reserveAmmo] Rounds available to reload with.
 * @property {boolean} [infiniteAmmo] Never consumes or tracks ammunition.
 * @property {boolean} [reloadEnabled] Whether the weapon can reload at all.
 * @property {number} [reloadDuration] Seconds one reload takes.
 * @property {boolean} [autoReloadWhenEmpty] Reloads by itself once the magazine runs dry.
 * @property {boolean} [friendlyFireEnabled] Whether shots may damage the owner's own side.
 * @property {number} [spreadDegrees] Cone half-angle override, in degrees.
 * @property {boolean} [streaming] Continuous cone weapon: fired by
 *   {@link ../systems/StreamFlameSystem.js} instead of spawning projectiles.
 * @property {number} [burnDamagePerSecond] Damage-over-time applied on hit.
 * @property {number} [burnDurationSeconds] Seconds that burn lasts.
 * @property {ReloadPresentation} [reloadPresentation] How the reload reads to
 *   the player (bala-by-bala style + final clack); omit for weapons that never
 *   reload.
 */

/** @type {Readonly<Record<string, WeaponArchetype>>} */
export const WEAPONS = Object.freeze({
  pistol: {
    name: 'Pistol',
    modelScale: 1,
    damage: 44,
    fireRate: 2,
    projectileSpeed: 62,
    range: 42,
    projectileRadius: 0.15,
    spread: 0.022,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.055,
    knockback: 1,
    fireMode: 'auto',
    magazineCapacity: 12,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 0.9,
    reloadPresentation: { style: 'magazine', finalClack: true },
    projectile: {
      prefab: 'bullet:pulse',
      color: PALETTE.bulletStandard,
      radius: 0.15,
      length: 1.1
    }
  },

  /**
   * Semi-auto sidearm with penetrating rounds. Slower than the pistol, harder
   * hit, and pierce enough to line up two bodies — the pay-off for landing
   * each click carefully. Mag-only under the default ammo policy.
   */
  revolver: {
    name: 'Revolver',
    modelScale: 1,
    damage: 58,
    fireRate: 1.5,
    projectileSpeed: 78,
    range: 48,
    projectileRadius: 0.14,
    spread: 0.012,
    pellets: 1,
    pierce: 3,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.09,
    knockback: 1.35,
    fireMode: 'semi',
    magazineCapacity: 6,
    startingAmmo: 6,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.0,
    reloadPresentation: { style: 'single_round', finalClack: true },
    projectile: {
      prefab: 'bullet:pulse',
      color: PALETTE.bulletRail,
      radius: 0.14,
      length: 1.4
    }
  },

  /**
   * The only weapon with a magazine: 15 rounds burn down quickly on held
   * fire and take 0.8 s to replace, which is the burst/reload rhythm the SMG
   * is balanced around.
   */
  smg: {
    name: 'SMG',
    modelScale: 1,
    damage: 24,
    fireRate: 17,
    projectileSpeed: 74,
    range: 34,
    projectileRadius: 0.12,
    spread: 0.14,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.035,
    knockback: 0.45,
    fireMode: 'auto',
    magazineCapacity: 15,
    startingAmmo: 15,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 0.8,
    reloadPresentation: { style: 'magazine', finalClack: true },
    projectile: {
      prefab: 'bullet:sting',
      color: PALETTE.bulletStandard,
      radius: 0.12,
      length: 0.9
    }
  },

  /**
   * Full-auto bullet hose. Huge magazine, low per-round damage, wide cone —
   * empties in a few seconds of held fire and forces a swap under the default
   * no-reload ammo policy.
   */
  gatling: {
    name: 'Gatling',
    modelScale: 1,
    damage: 12,
    fireRate: 16,
    projectileSpeed: 70,
    range: 32,
    projectileRadius: 0.11,
    spread: 0.18,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.15,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.028,
    knockback: 0.3,
    fireMode: 'auto',
    magazineCapacity: 72,
    startingAmmo: 72,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.4,
    reloadPresentation: { style: 'magazine', finalClack: true },
    muzzleParticles: 4,
    projectile: {
      prefab: 'bullet:sting',
      color: PALETTE.bulletHeavy,
      radius: 0.11,
      length: 0.85
    }
  },

  /**
   * Short-range burst weapon. Nine pellets make the raw numbers meaningless on
   * their own: what balances it is the per-target ceiling (one blast can never
   * dump nine pellets' worth of damage into a single body), the drop-off past
   * 60% of its short range, and a magazine that forces a 1.05 s pause every two
   * shots.
   */
  shotgun: {
    name: 'Shotgun',
    modelScale: 1,
    damage: 20,
    fireRate: 2.1,
    projectileSpeed: 54,
    range: 18,
    projectileRadius: 0.14,
    spread: 0.4,
    pellets: 9,
    pierce: 1,
    muzzleForward: 1.15,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.24,
    knockback: 0.5,
    fireMode: 'auto',
    maxDamagePerTarget: 80,
    falloffStart: 0.6,
    falloffMin: 0.6,
    muzzleParticles: 14,
    // Nine pellets leave the barrel on one round: a shot costs one shell.
    magazineCapacity: 2,
    startingAmmo: 2,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.05,
    reloadPresentation: { style: 'single_round', finalClack: true },
    projectile: {
      prefab: 'bullet:heavy',
      color: PALETTE.bulletHeavy,
      radius: 0.14,
      length: 0.7,
      flashColor: PALETTE.bulletHeavy,
      flashIntensity: 12,
      flashLife: 0.04
    }
  },

  /**
   * Long-range precision rifle. Low cadence, no spread, and enough pierce to
   * clear a whole column — the reward is entirely in lining shots up.
   */
  railgun: {
    name: 'Sniper',
    modelScale: 1,
    damage: 90,
    fireRate: 1.15,
    projectileSpeed: 190,
    range: 70,
    projectileRadius: 0.16,
    spread: 0,
    pellets: 1,
    // Pierces a whole column of enemies, which is the entire point of it.
    pierce: 6,
    muzzleForward: 1.3,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.38,
    knockback: 2,
    fireMode: 'auto',
    // Four rounds and a 1.2 s reload: the empty magazine is the drawback that
    // pays for the damage and the pierce.
    magazineCapacity: 4,
    startingAmmo: 4,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.2,
    reloadPresentation: { style: 'single_round', finalClack: true },
    laser: {
      color: PALETTE.alert,
      width: 0.045,
      opacity: 0.72
    },
    projectile: {
      prefab: 'bullet:rail',
      color: PALETTE.bulletRail,
      radius: 0.16,
      length: 3.4
    }
  },

  /**
   * Short-range area stream (issue #14). Nothing is spawned: the stream system
   * sweeps a cone every tick, so `projectileSpeed` and the `projectile` block
   * only describe how a round *would* look — they keep the archetype shape
   * uniform and give the prefab registry something to build.
   */
  flamethrower: {
    name: 'Flamethrower',
    modelScale: 1,
    damage: 10,
    // Ticks of the stream per second; also the cadence of the flame FX.
    fireRate: 24,
    projectileSpeed: 20,
    range: 12,
    projectileRadius: 0.34,
    spread: 0.24,
    pellets: 2,
    pierce: 0,
    // Low and close: the flame should look like it leaves the barrel, not the chest.
    muzzleForward: 0.8,
    muzzleHeight: 0.6,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    // Barely a rumble: a per-tick shake at 24 Hz would be a seizure, not feedback.
    shake: 0.02,
    knockback: 0,
    streaming: true,
    burnDamagePerSecond: 6,
    burnDurationSeconds: 2.5,
    magazineCapacity: 160,
    startingAmmo: 160,
    infiniteAmmo: false,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.3,
    reloadPresentation: { style: 'canister', finalClack: true },
    projectile: {
      prefab: 'bullet:flame',
      color: PALETTE.bulletFlame,
      radius: 0.34,
      length: 1.5
    }
  },

  /**
   * Enemy-carried Cinder. Same weapon, enemy layers, no magazine to manage —
   * an AI that had to reload would just stand still looking broken.
   */
  flamethrower_enemy: {
    name: 'Flamethrower',
    modelScale: 1,
    damage: 8,
    fireRate: 24,
    projectileSpeed: 20,
    range: 9,
    projectileRadius: 0.34,
    spread: 0.24,
    pellets: 2,
    pierce: 0,
    muzzleForward: 0.8,
    muzzleHeight: 0.6,
    projectileLayer: Layer.ENEMY_BULLET,
    // Bullets cross-hit enemies (issue #75); a flame cone does not. This one is
    // a stream, and a burner torching its own squad every time it fires is a
    // balance decision, not the friendly-fire rule that issue asked for.
    projectileMask: Layer.WALL | Layer.PLAYER,
    shake: 0,
    knockback: 0,
    streaming: true,
    burnDamagePerSecond: 4,
    burnDurationSeconds: 2,
    infiniteAmmo: true,
    reloadEnabled: false,
    reloadDuration: 1.3,
    projectile: {
      prefab: 'bullet:flame',
      color: PALETTE.bulletFlame,
      radius: 0.34,
      length: 1.5
    }
  },

  /**
   * Heavy explosive launcher. Scarce, slow and loud: one missile per reload,
   * a single-round tube, and a blast that hurts whoever fired it. Everything
   * about it is tuned to make the player choose a moment instead of spraying.
   */
  rocket_launcher: {
    name: 'Rocket Launcher',
    modelScale: 1,
    category: WeaponCategory.EXPLOSIVE_HEAVY,
    // Direct hit damage. The blast below is what actually clears a group.
    damage: 84,
    // 1/0.75 = 1.333 s between shots: each missile is a decision.
    fireRate: 0.75,
    // Slow enough to read in flight, which is the whole tell of the weapon.
    projectileSpeed: 28,
    range: 46,
    projectileRadius: 0.28,
    // Near zero: the difficulty is the travel time, never the aim.
    spread: 0.01,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.PLAYER_BULLET,
    projectileMask: Layer.WALL | Layer.ENEMY,
    shake: 0.18,
    knockback: 0,
    // 46 / 28 = 1.64 s of flight, so this only catches a missile that somehow
    // outlives its own range check.
    projectileLifetime: 3,
    magazineCapacity: 1,
    startingAmmo: 1,
    reloadEnabled: true,
    autoReloadWhenEmpty: true,
    reloadDuration: 1.4,
    reloadPresentation: { style: 'heavy_round', finalClack: true },
    explosive: {
      radius: 5,
      damage: 190,
      // 0.35 rather than 0.5: at 100 max health a point-blank shot must be a
      // painful mistake, not a suicide.
      selfDamageMultiplier: 0.35,
      mask: Layer.WALL | Layer.ENEMY | Layer.PLAYER,
      shake: 0.55
    },
    trail: {
      distance: 0.3,
      color: PALETTE.explosion,
      size: 0.35,
      life: 0.5
    },
    projectile: {
      prefab: 'bullet:rocket',
      color: PALETTE.bulletRocket,
      radius: 0.28,
      length: 2
    }
  },

  /**
   * Enemy Pulse (issue #57). A single, legible shot the player can read and
   * step around: slower round than the player's Pulse, low damage, and a lazy
   * cadence. Enemy layers, no magazine — an AI that reloaded would just freeze.
   */
  pistol_enemy: {
    name: 'Pulse',
    modelScale: 1,
    damage: 16,
    fireRate: 2.2,
    projectileSpeed: 46,
    range: 34,
    projectileRadius: 0.16,
    spread: 0.05,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 0.8,
    fireMode: 'auto',
    infiniteAmmo: true,
    reloadEnabled: false,
    projectile: {
      prefab: 'bullet:pulse_enemy',
      color: PALETTE.enemyBulletStandard,
      radius: 0.16,
      length: 1
    }
  },

  /**
   * Enemy Sting. Keeps the SMG's fast, spitting cadence but with a wider cone
   * and a fraction of the per-round damage — pressure, not a delete button. The
   * ranged template paces the trigger; the low damage is what keeps infinite
   * ammo fair.
   */
  smg_enemy: {
    name: 'Sting',
    modelScale: 1,
    damage: 10,
    fireRate: 6,
    projectileSpeed: 66,
    range: 30,
    projectileRadius: 0.12,
    spread: 0.14,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 0.45,
    fireMode: 'auto',
    infiniteAmmo: true,
    reloadEnabled: false,
    projectile: {
      prefab: 'bullet:sting_enemy',
      color: PALETTE.enemyBulletStandard,
      radius: 0.12,
      length: 0.9
    }
  },

  /**
   * Enemy Splitter. Few pellets and a short muzzle-to-target window: it only
   * hurts up close, so it punishes a player who lets a shotgunner crowd them and
   * whiffs entirely at range.
   */
  shotgun_enemy: {
    name: 'Splitter',
    modelScale: 1,
    damage: 10,
    fireRate: 1,
    projectileSpeed: 46,
    range: 15,
    projectileRadius: 0.14,
    spread: 0.22,
    pellets: 5,
    pierce: 0,
    muzzleForward: 1.15,
    muzzleHeight: 0.85,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 0.5,
    fireMode: 'auto',
    maxDamagePerTarget: 44,
    falloffStart: 0.55,
    falloffMin: 0.5,
    infiniteAmmo: true,
    reloadEnabled: false,
    projectile: {
      prefab: 'bullet:heavy_enemy',
      color: PALETTE.enemyBulletHeavy,
      radius: 0.14,
      length: 0.7,
      flashColor: PALETTE.enemyBulletHeavy,
      flashIntensity: 12,
      flashLife: 0.04
    }
  },

  /**
   * Enemy Lance. High damage on a very slow cadence — but the round is
   * deliberately *slow in flight* (60 vs the player's 190), so the sniper's shot
   * is dodgeable on sight instead of a hitscan the player can never react to.
   * The wind-up (the enemy visibly turning to aim) is the telegraph.
   */
  railgun_enemy: {
    name: 'Lance',
    modelScale: 1,
    damage: 60,
    fireRate: 0.6,
    projectileSpeed: 60,
    range: 60,
    projectileRadius: 0.18,
    spread: 0.008,
    pellets: 1,
    pierce: 1,
    muzzleForward: 1.3,
    muzzleHeight: 0.85,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 1.6,
    fireMode: 'auto',
    projectileLifetime: 1.6,
    infiniteAmmo: true,
    reloadEnabled: false,
    laser: {
      color: PALETTE.enemyBulletRail,
      width: 0.055,
      opacity: 0.82
    },
    projectile: {
      prefab: 'bullet:rail_enemy',
      color: PALETTE.enemyBulletRail,
      radius: 0.18,
      length: 3
    }
  },

  /**
   * Enemy Comet (issue #57 — the highest-risk row). Fired through the same
   * owner-agnostic rocket path as the player's, so nothing but data changes.
   * The missile is slow and reads in flight; its blast is set to chain into
   * other enemies on purpose (`mask` includes `ENEMY`), while the shooter itself
   * takes no self-damage so a rocketeer never awkwardly suicides.
   */
  rocket_launcher_enemy: {
    name: 'Comet',
    modelScale: 1,
    category: WeaponCategory.EXPLOSIVE_HEAVY,
    damage: 50,
    fireRate: 0.5,
    projectileSpeed: 22,
    range: 44,
    projectileRadius: 0.28,
    spread: 0.02,
    pellets: 1,
    pierce: 0,
    muzzleForward: 1.05,
    muzzleHeight: 0.85,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 0,
    projectileLifetime: 3,
    infiniteAmmo: true,
    reloadEnabled: false,
    explosive: {
      radius: 4.5,
      damage: 110,
      // The shooter is exempt from its own blast (an enemy gibbing itself reads
      // as a bug); other enemies caught in it are not, which is the intended chain.
      selfDamageMultiplier: 0,
      mask: Layer.WALL | Layer.ENEMY | Layer.PLAYER,
      shake: 0
    },
    trail: {
      distance: 0.3,
      color: PALETTE.enemyBulletRocket,
      size: 0.35,
      life: 0.5
    },
    projectile: {
      prefab: 'bullet:rocket_enemy',
      color: PALETTE.enemyBulletRocket,
      radius: 0.28,
      length: 2
    }
  },

  /** Enemy-only sidearm carried by spitters. */
  spitter: {
    name: 'Semi-Auto',
    modelScale: 1,
    // Medium per-bullet damage: the threat is the three-round tap, not one hit.
    damage: 14,
    // Cadence between burst starts; the rounds inside a burst are spaced below.
    fireRate: 1.25,
    burstCount: 3,
    burstInterval: 0.07,
    projectileSpeed: 26,
    range: 24,
    projectileRadius: 0.22,
    spread: 0.08,
    pellets: 1,
    pierce: 0,
    muzzleForward: 0.9,
    muzzleHeight: 0.8,
    projectileLayer: Layer.ENEMY_BULLET,
    // Enemy rounds are safe for their owner, not for every enemy (issue #75).
    projectileMask: Layer.WALL | Layer.PLAYER | Layer.ENEMY,
    shake: 0,
    knockback: 0.35,
    fireMode: 'auto',
    // Compact belt, then a long dead moment while it reloads.
    magazineCapacity: 30,
    reloadEnabled: true,
    reloadDuration: 3,
    projectile: {
      prefab: 'bullet:acid',
      color: PALETTE.bulletAcid,
      radius: 0.22,
      length: 0.6
    }
  },

  /**
   * Melee fallback: automatic short-range swing when the player is unarmed.
   * No projectile, no ammo — just a hitscan arc in front of the player.
   * (Issue #96)
   */
  /**
   * The weapon you always have. It is a real equipped `Weapon` like any other —
   * what makes it the fallback, undroppable and unthrowable is this row, not a
   * `key === 'knife'` test scattered across the systems that care (issue #122).
   */
  knife: {
    name: 'Knife',
    modelScale: 1,
    damage: 30,
    fireRate: 3,
    range: 2.5,
    meleeArcDegrees: 120,
    muzzleForward: 1.2,
    muzzleHeight: 0.8,
    shake: 0.08,
    fireMode: 'auto',
    type: 'melee',
    // A blade does not run dry, and a magazine on it would only give the HUD
    // something meaningless to draw.
    infiniteAmmo: true,
    fallback: true,
    droppable: false,
    throwable: false,
    /** How long one slash takes, which is also how long the arc FX lives. */
    swingSeconds: 0.18
  }
});

/**
 * Rejects an archetype whose numbers cannot produce a working weapon.
 *
 * The table is data, so a typo here is silent: a `fireRate` of 0 divides by
 * zero into an infinite cooldown and a negative `spread` fires backwards. The
 * check runs once at import, which turns those into a startup error instead of
 * a bug report.
 *
 * @param {string} key Archetype key.
 * @param {WeaponArchetype} archetype Stats to check.
 * @returns {void}
 * @throws {RangeError} If any stat is out of range.
 */
function validateArchetype(key, archetype) {
  /** @param {boolean} ok @param {string} rule */
  const require = (ok, rule) => {
    if (!ok) throw new RangeError(`Weapon "${key}" is invalid: ${rule}.`);
  };

  require(archetype.damage >= 0, 'damage must be >= 0');
  require(archetype.modelScale > 0, 'modelScale must be > 0');
  require(archetype.fireRate > 0, 'fireRate must be > 0');
  require(archetype.range > 0, 'range must be > 0');
  if (archetype.burstCount !== undefined) {
    require(Number.isInteger(archetype.burstCount), 'burstCount must be an integer');
    require(archetype.burstCount >= 1, 'burstCount must be >= 1');
  }
  if (archetype.burstInterval !== undefined) {
    require(Number.isFinite(archetype.burstInterval), 'burstInterval must be finite');
    require(archetype.burstInterval >= 0, 'burstInterval must be >= 0');
  }
  if ((archetype.burstCount ?? 1) > 1) {
    require((archetype.burstInterval ?? 0) > 0, 'burstInterval must be > 0 for bursts');
  }

  // Melee weapons (knife) have no projectile: skip projectile-specific checks.
  if (archetype.type !== 'melee') {
    require(archetype.projectileSpeed > 0, 'projectileSpeed must be > 0');
    require(archetype.spread >= 0, 'spread must be >= 0');
    require(archetype.pellets >= 1, 'pellets must be >= 1');
    require(archetype.pierce >= 0, 'pierce must be >= 0');
  }

  const magazine = archetype.magazineCapacity ?? 0;
  require(magazine >= 0, 'magazineCapacity must be >= 0');
  if (archetype.reserveAmmo !== undefined) {
    require(Number.isFinite(archetype.reserveAmmo), 'reserveAmmo must be finite');
    require(archetype.reserveAmmo >= 0, 'reserveAmmo must be >= 0');
  }
  if (magazine > 0 && archetype.infiniteAmmo !== true) {
    require((archetype.reloadDuration ?? 0) > 0, 'a magazine needs a reloadDuration > 0');
    require(
      (archetype.startingAmmo ?? magazine) <= magazine,
      'startingAmmo cannot exceed magazineCapacity'
    );
  }
}

for (const [key, archetype] of Object.entries(WEAPONS)) validateArchetype(key, archetype);

/** Weapon the player starts every run with. */
export const DEFAULT_WEAPON = 'pistol';

/**
 * The weapon a carrier falls back to with nothing else in hand, derived from the
 * table rather than named here — so which weapon plays that role stays a data
 * decision (issue #122).
 * @type {string | null}
 */
export const FALLBACK_WEAPON =
  Object.keys(WEAPONS).find((key) => WEAPONS[key].fallback === true) ?? null;

/** Weapons that can be rolled as the first weapon of a new run. */
export const STARTING_WEAPONS = Object.freeze(['pistol', 'smg', 'shotgun', 'spitter']);

/**
 * Picks the weapon a brand-new run starts with.
 *
 * @param {() => number} [random] Random source returning 0..1.
 * @returns {string} Key into {@link WEAPONS}.
 */
export function randomStartingWeapon(random = Math.random) {
  const index = Math.min(
    STARTING_WEAPONS.length - 1,
    Math.max(0, Math.floor(random() * STARTING_WEAPONS.length))
  );
  return STARTING_WEAPONS[index];
}

/**
 * Checks one archetype against the invariants every firing system assumes.
 *
 * Bad content is a data bug, and a data bug that only shows up as a missing
 * bullet three levels in is expensive; this turns it into a load-time throw.
 *
 * @param {string} key Archetype key, used in the error message.
 * @param {WeaponArchetype} archetype Stat block to check.
 * @returns {void}
 * @throws {RangeError} When a stat is outside its legal range.
 */
export function validateWeaponArchetype(key, archetype) {
  /**
   * @param {boolean} condition Invariant that must hold.
   * @param {string} message What was violated.
   */
  const require = (condition, message) => {
    if (!condition) throw new RangeError(`Weapon "${key}": ${message}.`);
  };

  require(archetype.damage >= 0, 'damage must be >= 0');
  require(archetype.modelScale > 0, 'modelScale must be > 0');
  require(archetype.fireRate > 0, 'fireRate must be > 0');
  require(archetype.range > 0, 'range must be > 0');
  if (archetype.burstCount !== undefined) {
    require(Number.isInteger(archetype.burstCount), 'burstCount must be an integer');
    require(archetype.burstCount >= 1, 'burstCount must be >= 1');
  }
  if (archetype.burstInterval !== undefined) {
    require(Number.isFinite(archetype.burstInterval), 'burstInterval must be finite');
    require(archetype.burstInterval >= 0, 'burstInterval must be >= 0');
  }
  if ((archetype.burstCount ?? 1) > 1) {
    require((archetype.burstInterval ?? 0) > 0, 'burstInterval must be > 0 for bursts');
  }

  // Melee weapons have no projectile: skip projectile-specific checks.
  if (archetype.type !== 'melee') {
    require(archetype.projectileSpeed > 0, 'projectileSpeed must be > 0');
    require(archetype.spread >= 0, 'spread must be >= 0');
    require(archetype.pellets >= 1, 'pellets must be >= 1');
    require(archetype.pierce >= 0, 'pierce must be >= 0');
  }

  const { explosive } = archetype;
  if (archetype.reloadEnabled) {
    require(archetype.magazineCapacity > 0, 'magazineCapacity must be > 0 when reloadEnabled');
  }
  if (archetype.magazineCapacity !== undefined) {
    require(archetype.magazineCapacity >= 0, 'magazineCapacity must be >= 0');
    const starting = archetype.startingAmmo ?? archetype.magazineCapacity;
    require(starting >= 0, 'startingAmmo must be >= 0');
    require(starting <= archetype.magazineCapacity, 'startingAmmo must fit the magazine');
  }
  if (archetype.reloadDuration !== undefined) {
    require(archetype.reloadDuration >= 0, 'reloadDuration must be >= 0');
  }
  if (archetype.reserveAmmo !== undefined) {
    require(Number.isFinite(archetype.reserveAmmo), 'reserveAmmo must be finite');
    require(archetype.reserveAmmo >= 0, 'reserveAmmo must be >= 0');
  }
  if (explosive) {
    require(explosive.radius > 0, 'explosive.radius must be > 0');
    require(explosive.damage >= 0, 'explosive.damage must be >= 0');
    require(explosive.selfDamageMultiplier >= 0, 'explosive.selfDamageMultiplier must be >= 0');
  }
}

// Content is validated once, at import, so a bad row can never reach a run.
for (const [key, archetype] of Object.entries(WEAPONS)) validateWeaponArchetype(key, archetype);
